import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import {
  canUseReaderCache,
  knownReaderSceneVersion,
  persistReaderEnvelope,
  readReaderCache,
  readSceneEnvelope,
  sceneEnvelopeError,
  sceneVersion,
} from "../reader-envelope";
import type { components } from "../schema";

export type Scene = components["schemas"]["Scene"];

export type UseCurrentSceneResult = UseQueryResult<Scene, ApiError>;

// GET /playlines/{playlineId}/scenes/current を呼び、現在のシーンを返す query。
// サーバー側でシーンが進行しない限り内容が変わらない冪等な API のため、
// 有効なcacheのstaleTimeをInfinityにし、旧fixed/不正cacheはオンライン再取得する。
// playlineId が未確定
// （null）の段階ではクエリ自体を無効化し、リクエストが飛ばないようにする。
export function useCurrentScene(
  playlineId: string | null,
  generationMode?: string,
): UseCurrentSceneResult {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const result = useQuery<Scene, ApiError>({
    queryKey: ["currentScene", playlineId],
    enabled: playlineId !== null,
    staleTime: (query) => sceneEnvelopeError(query.state.data, generationMode) === null ? Infinity : 0,
    select: (data) => readSceneEnvelope(data, generationMode),
    // design.md「Performance」セクションに従い 5 分でガベージコレクトする。
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      // enabled: false の状態なら queryFn は呼ばれないため、ここに到達した
      // 時点で playlineId は必ず非 null。型を狭めるための保険的なガード。
      if (playlineId === null) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useCurrentScene queryFn invoked without playlineId",
        });
      }

      const memoryScene = queryClient.getQueryData<unknown>(["currentScene", playlineId]);
      const memoryVersion = sceneEnvelopeError(memoryScene, generationMode) === null ? sceneVersion(memoryScene) : undefined;
      try {
        const { data, error } = await client.GET(
          "/playlines/{playlineId}/scenes/current",
          {
            params: { path: { playlineId } },
          },
        );

        if (error !== undefined) {
          throw new ApiError(
            typeof (error as { status?: number }).status === "number"
              ? (error as { status: number }).status
              : 0,
            error as unknown as ApiError["problem"],
          );
        }
        // HTTP拒否を先に伝え、成功保存前だけ同Playlineの永続pinも照合する。
        const expectedVersion = await knownReaderSceneVersion(playlineId, memoryScene, generationMode);
        const scene = readSceneEnvelope(data, generationMode, expectedVersion);
        persistReaderEnvelope("scene", playlineId, scene);
        return scene;
      } catch (err) {
        if (!canUseReaderCache(err)) throw err;
        const cached = await readReaderCache("scene", playlineId);
        if (cached !== null) return readSceneEnvelope(cached, generationMode, memoryVersion);
        throw err;
      }
    },
  });
  // refetch失敗時もQueryは古いdataを保持するため、拒否の描画を優先する。
  if (result.isError) return { ...result, data: undefined, isLoadingError: true, isRefetchError: false };
  return result;
}
